import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as interFace from "../../Config/interface.config";
import APIRequest from "../../API";
import * as APIPATHS from "../../API/path";
import toast from "react-hot-toast";

interface AddComment {
  post_id: string;
  comment: interFace.SingleComment;
}

const initialState: interFace.PostDetails = {
  posts: [],
  feed: {
    limit: 10,
    page: 1,
    results: [],
    hasMore: true,
  },
  loading: false,
};

export const createNewPost = createAsyncThunk<any, interFace.Post>(
  "post/create-new-post",
  async (postData, { rejectWithValue, dispatch }) => {
    try {
      toast.loading("Creating new post...");
      const response: any = await APIRequest(APIPATHS.createPost, postData);
      toast.remove();
      if (response.error) throw new Error(response.error.message);
      toast.success("Post created successfully", { duration: 3000 });
      void dispatch(feedHandler({ limit: 10, page: 1, isIntro: false }));
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ reason: error.message ?? error.response.data });
    }
  }
);

export const updatePost = createAsyncThunk<any, interFace.EditPostPayload>(
  "post/update-post",
  async (postData, { rejectWithValue, dispatch }) => {
    try {
      if (!postData._id) return toast.error("Post ID is in-valid.");

      toast.loading("Updating post...");
      let path = { ...APIPATHS.updatePost };

      path.url = path.url.replace("{{postId}}", postData._id);
      delete postData["_id"];
      delete postData["files"];
      const response: any = await APIRequest(path, postData);
      toast.remove();

      if (response.error) throw new Error(response.error.message);
      toast.success("Post updated successfully", { duration: 3000 });
      // dispatch(feedHandler({ limit: 1000, page: 1 }))
      return response.data;
    } catch (error: any) {
      return rejectWithValue({ reason: error.message ?? error.response.data });
    }
  }
);

export const feedHandler = createAsyncThunk<
  any,
  { limit: number; page: number; creator?: string; isIntro?: boolean }
>("post/feed", async (data, { rejectWithValue, getState }) => {
  try {
    const response: any = await APIRequest(APIPATHS.myFeed, {}, { ...data });

    if (response.error) {
      toast.error(response.error);
      return rejectWithValue(response.error);
    }
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message ?? error.response.data);
  }
});

export const postDelete = createAsyncThunk<any, { postId: string }>(
  "post/delete/feed",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      toast.loading("Removing your post...");

      const apiConfig = { ...APIPATHS.postDelete };
      apiConfig.url = apiConfig.url.replace("{{postId}}", data.postId);

      const response: any = await APIRequest(apiConfig);
      toast.remove();

      if (response.error) {
        return rejectWithValue(response.error);
      }

      toast.success("Post removed successfully", { duration: 2500 });
      dispatch(feedHandler({ limit: 10, page: 1, isIntro: false }));
      return response.data;
    } catch (error: any) {
      toast.remove();
      return rejectWithValue({
        message: error?.message ?? "Unable to remove post",
      });
    }
  }
);

export const requestPostRemovalReview = createAsyncThunk<
  any,
  { postId: string; urgent: boolean; note?: string }
>(
  "post/request-removal-review",
  async (data, { rejectWithValue, dispatch }) => {
    try {
      toast.loading("Sending deletion-unlock request...");

      const apiConfig = { ...APIPATHS.requestPostRemoval };
      apiConfig.url = apiConfig.url.replace("{{postId}}", data.postId);

      const response: any = await APIRequest(apiConfig, {
        urgent: data.urgent,
        note: data.note ?? "",
      });
      toast.remove();

      if (response.error) {
        return rejectWithValue(response.error);
      }

      toast.success(response.data?.message ?? "Removal request sent", {
        duration: 3500,
      });

      if (data.urgent) {
        dispatch(feedHandler({ limit: 1000, page: 1 }));
      }

      return response.data;
    } catch (error: any) {
      toast.remove();
      return rejectWithValue({
        message: error?.message ?? "Unable to send removal request",
      });
    }
  }
);

const postSlice = createSlice({
  name: "post",
  initialState,
  reducers: {
    updateFeedPostComment(state, { payload }: PayloadAction<AddComment>) {
      const post = state.feed.results.find(p => p._id === payload.post_id);
      if (post) {
        post.comments.push(payload.comment);
      }
    },

    updatePostLike(
      state,
      { payload }: PayloadAction<interFace.LikePostResponse>
    ) {
      const post = state.feed.results.find(p => p._id === payload._id);
      if (!post) return;

      const exists = post.likeCount.some(like => like._id === payload._id);
      if (!exists) {
        post.likeCount.push(payload);
      }
    },

    clearFeed(state) {
      state.feed = {
        limit: 10,
        page: 1,
        results: [],
        hasMore: true,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(createNewPost.fulfilled, (state, action) => {
        const newPost = action.payload as interFace.FeedPosts | undefined;

        // Intro posts belong to the profile intro surface, not the main feed.
        if (!newPost || newPost.isIntro) return;

        const newPostId = newPost.id || newPost._id;
        const alreadyInFeed = state.feed.results.some(
          (post) => (post.id || post._id) === newPostId
        );

        if (!alreadyInFeed) {
          // The creator always has access to their newly-created post. The
          // regular feed endpoint supplies the same flag on a later refresh.
          state.feed.results.unshift({ ...newPost, isLocked: false });
        }
      })
      .addCase(feedHandler.pending, (state) => {
        state.loading = true;
      })
      .addCase(feedHandler.fulfilled, (state, action) => {
        const isFirstPage = action.meta.arg.page === 1;

        if (isFirstPage) {
          state.feed = action.payload;
        } else {
          const existingPostIds = new Set(
            state.feed.results.map((post) => post.id || post._id)
          );
          const newResults = action.payload.results.filter(
            (post: interFace.FeedPosts) => !existingPostIds.has(post.id || post._id)
          );

          state.feed.results.push(...newResults);
          state.feed.page = action.payload.page;
          state.feed.hasMore = action.payload.hasMore;
        }

        state.loading = false;
      })
      .addCase(feedHandler.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { updateFeedPostComment, updatePostLike } = postSlice.actions;
export default postSlice.reducer;
